import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get, ref, update } from 'firebase/database';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { auth, database, storage } from '../firebase';

type ProjectType = 'Global' | 'Standard';
type ScopeType = 'full' | 'preview';
type LanguageType = 'es' | 'en';
type ToastType = 'neutral' | 'success' | 'error' | 'warning';
type StatusContext = 'load' | 'type' | 'run' | 'send' | 'general';
type PromptStatus = 'Ejecutado' | 'Regenerado' | 'Pendiente';

type PromptTemplate = {
  id: number;
  nombre: string;
  archivo: string;
  prompt: string;
  inputs: string[];
  outputs: string[];
  inputs_img: string[];
};

type FlowPrompt = PromptTemplate & {
  scope: ScopeType;
  rowKey: string;
};

type QueueNode = {
  projectsGlobal?: unknown;
  projectsStandard?: unknown;
  run?: unknown;
};

type QueuePreviewNode = {
  projects?: unknown;
  run?: unknown;
};

type RunPromptResponse = {
  regenerationId?: unknown;
};

type SendResponse = {
  regenerationId?: unknown;
  missingAssets?: unknown;
};

type GenerateAssets = {
  brandPdf: boolean;
  rightsPdf: boolean;
  contentPdf: boolean;
  zip: boolean;
  logoVariants: boolean;
};

type SourceType = 'session' | 'base' | 'preview' | 'missing';

type PromptArtifactState = {
  existsRoot: boolean;
  existsSession: boolean;
  existsPreview: boolean;
};

type ArtifactLoadResult = {
  nextArtifacts: Record<string, PromptArtifactState>;
  nextRootValues: Record<string, string>;
  nextSessionValues: Record<string, string>;
  nextPreviewValues: Record<string, string>;
  nextImageAvailability: Record<string, PromptArtifactState>;
  nextManualOverrides: Record<string, string>;
};

const partialGenerationEndpoint = 'https://generar-partial-slyn3k2c5q-uc.a.run.app';

const parsePromptTemplates = (value: Record<string, unknown> | null): PromptTemplate[] => {
  if (!value) {
    return [];
  }
  return Object.entries(value)
    .map(([key, rawItem], index) => {
      const item = (rawItem ?? {}) as Record<string, unknown>;
      const rawId = Number(item.id ?? Number(key));
      const id = Number.isFinite(rawId) && rawId > 0 ? rawId : index + 1;
      return {
        id,
        nombre: String(item.nombre ?? item.name ?? '').trim(),
        archivo: String(item.archivo ?? '').trim(),
        prompt: String(item.prompt ?? '').trim(),
        inputs: Array.isArray(item.inputs)
          ? item.inputs.map((entry) => String(entry ?? '').trim()).filter(Boolean)
          : [],
        outputs: Array.isArray(item.outputs)
          ? item.outputs.map((entry) => String(entry ?? '').trim()).filter(Boolean)
          : [],
        inputs_img: Array.isArray(item.inputs_img)
          ? item.inputs_img.map((entry) => String(entry ?? '').trim()).filter(Boolean)
          : [],
      };
    })
    .sort((a, b) => a.id - b.id);
};

const extractPromptVariables = (template: string) => {
  const regex = /\{([^\s{}\r\n]+)\}/g;
  const unique = new Set<string>();
  let match = regex.exec(template);
  while (match) {
    const key = match[1]?.trim();
    if (key) {
      unique.add(key);
    }
    match = regex.exec(template);
  }
  return Array.from(unique);
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const containsProjectId = (value: unknown, projectId: string): boolean => {
  const normalized = projectId.trim();
  if (!normalized) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((entry) => String(entry ?? '').trim() === normalized);
  }
  if (!isObjectRecord(value)) {
    return false;
  }
  return Object.values(value).some((entry) => String(entry ?? '').trim() === normalized);
};

const isRunningProject = (runNode: unknown, projectId: string) => {
  if (!isObjectRecord(runNode)) {
    return false;
  }
  return Object.keys(runNode).some((key) => key.trim() === projectId.trim());
};

const getRegenerationIdsFromProject = (projectValue: Record<string, unknown>) => {
  const ids = new Set<string>();
  Object.keys(projectValue).forEach((key) => {
    const costMatch = key.match(/^Regeneration_(.+)$/i);
    if (costMatch?.[1]) {
      ids.add(costMatch[1]);
    }
    const startMatch = key.match(/^start_regeneration_(.+)$/i);
    if (startMatch?.[1]) {
      ids.add(startMatch[1]);
    }
    const endMatch = key.match(/^end_regeneration_(.+)$/i);
    if (endMatch?.[1]) {
      ids.add(endMatch[1]);
    }
  });
  return Array.from(ids).sort();
};

const getFolderId = (projectId: string, regenerationId: string) => {
  const p = projectId.trim();
  const r = regenerationId.trim();
  if (!p) {
    return '';
  }
  return r ? `${p}/${r}` : p;
};

const formatValuePreview = (value: unknown) => {
  if (value == null) {
    return '-';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const toBase64Content = (dataUrl: string) => {
  const commaIndex = dataUrl.indexOf(',');
  if (commaIndex < 0) {
    return dataUrl;
  }
  return dataUrl.slice(commaIndex + 1);
};

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });

const getArtifactFolder = (archivo: string) => {
  const lower = archivo.toLowerCase();
  if (lower.endsWith('.json')) {
    return 'JSONS';
  }
  if (lower.endsWith('.png')) {
    return 'IMGS';
  }
  if (lower.endsWith('.pdf')) {
    return 'PDF';
  }
  return '';
};

const buildArtifactPath = (folderId: string, archivo: string) => {
  const artifactFolder = getArtifactFolder(archivo);
  if (!artifactFolder) {
    return `${folderId}/${archivo}`;
  }
  return `${folderId}/${artifactFolder}/${archivo}`;
};

const getFileUrlIfExists = async (fullPath: string) => {
  try {
    return await getDownloadURL(storageRef(storage, fullPath));
  } catch (error) {
    return '';
  }
};

const readJsonTopLevelMap = async (url: string) => {
  if (!url) {
    return {} as Record<string, string>;
  }
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return {};
    }
    const payload = (await response.json()) as unknown;
    if (!isObjectRecord(payload)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(payload).map(([key, value]) => [key, typeof value === 'string' ? value : String(value)])
    );
  } catch (error) {
    return {};
  }
};

function ManualGenerationSection() {
  const [projectId, setProjectId] = useState('');
  const [regenerationId, setRegenerationId] = useState('');
  const [projectType, setProjectType] = useState<ProjectType | null>(null);
  const [selectedPreview, setSelectedPreview] = useState<number | null>(null);
  const [regenerationIds, setRegenerationIds] = useState<string[]>([]);

  const [projectNombre, setProjectNombre] = useState('');
  const [projectEmail, setProjectEmail] = useState('');
  const [projectLanguage, setProjectLanguage] = useState<LanguageType>('es');

  const [prompts, setPrompts] = useState<FlowPrompt[]>([]);
  const [selectedPromptKey, setSelectedPromptKey] = useState('');

  const [promptArtifacts, setPromptArtifacts] = useState<Record<string, PromptArtifactState>>({});
  const [rootOutputValues, setRootOutputValues] = useState<Record<string, string>>({});
  const [sessionOutputValues, setSessionOutputValues] = useState<Record<string, string>>({});
  const [previewOutputValues, setPreviewOutputValues] = useState<Record<string, string>>({});
  const [imageAvailability, setImageAvailability] = useState<Record<string, PromptArtifactState>>({});
  const [infoDraftValues, setInfoDraftValues] = useState<Record<string, string>>({});
  const [infoSourceValues, setInfoSourceValues] = useState<Record<string, string>>({});
  const [textForcedOverrides, setTextForcedOverrides] = useState<Record<string, string>>({});
  const [imageForcedOverrides, setImageForcedOverrides] = useState<
    Record<string, { fileName: string; base64: string }>
  >({});
  const [inputImageOverride, setInputImageOverride] = useState<{
    fileName: string;
    base64: string;
    previewUrl: string;
  } | null>(null);

  const [promptOverride, setPromptOverride] = useState('');
  const [sendName, setSendName] = useState('');
  const [sendEmail, setSendEmail] = useState('');
  const [sendLanguage, setSendLanguage] = useState<LanguageType>('es');
  const [generateAssets, setGenerateAssets] = useState<GenerateAssets>({
    brandPdf: true,
    rightsPdf: true,
    contentPdf: true,
    zip: true,
    logoVariants: false,
  });

  const [isQueueLocked, setIsQueueLocked] = useState(false);
  const [queueLockMessage, setQueueLockMessage] = useState('');
  const [isInfoExpanded, setIsInfoExpanded] = useState(true);
  const [isPromptsExpanded, setIsPromptsExpanded] = useState(true);
  const [emailAssetsBrandPdfUrl, setEmailAssetsBrandPdfUrl] = useState('');
  const [emailAssetsRightsPdfUrl, setEmailAssetsRightsPdfUrl] = useState('');
  const [emailAssetsContentPdfUrl, setEmailAssetsContentPdfUrl] = useState('');
  const [emailAssetsZipUrl, setEmailAssetsZipUrl] = useState('');

  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState<ToastType>('neutral');
  const [statusContext, setStatusContext] = useState<StatusContext>('general');
  const [isLoading, setIsLoading] = useState(false);
  const [isRunningPrompt, setIsRunningPrompt] = useState(false);
  const [isSendingFinal, setIsSendingFinal] = useState(false);
  const [isInfoLoaded, setIsInfoLoaded] = useState(false);
  const [requiresTypeSelection, setRequiresTypeSelection] = useState(false);
  const [manualTypeSelection, setManualTypeSelection] = useState<ProjectType>('Global');
  const [isSavingProjectType, setIsSavingProjectType] = useState(false);

  const selectedPrompt = useMemo(
    () => prompts.find((item) => item.rowKey === selectedPromptKey) ?? null,
    [prompts, selectedPromptKey]
  );
  const hasEmailAssets = Boolean(
    emailAssetsBrandPdfUrl &&
      emailAssetsRightsPdfUrl &&
      emailAssetsContentPdfUrl &&
      emailAssetsZipUrl
  );

  const resolveValue = useCallback(
    (key: string): { value: unknown; source: SourceType } => {
      if (Object.prototype.hasOwnProperty.call(sessionOutputValues, key)) {
        return { value: sessionOutputValues[key], source: 'session' };
      }
      if (Object.prototype.hasOwnProperty.call(rootOutputValues, key)) {
        return { value: rootOutputValues[key], source: 'base' };
      }
      if (Object.prototype.hasOwnProperty.call(previewOutputValues, key)) {
        return { value: previewOutputValues[key], source: 'preview' };
      }
      return { value: undefined, source: 'missing' };
    },
    [sessionOutputValues, rootOutputValues, previewOutputValues]
  );

  const allInputKeys = useMemo(() => {
    const keys = new Set<string>();
    prompts.forEach((prompt) => {
      prompt.inputs.forEach((key) => keys.add(key));
      extractPromptVariables(prompt.prompt).forEach((key) => keys.add(key));
    });
    return Array.from(keys);
  }, [prompts]);

  const allOutputKeys = useMemo(() => {
    const keys = new Set<string>();
    prompts.forEach((prompt) => {
      if (prompt.archivo.toLowerCase().endsWith('.json')) {
        prompt.outputs.forEach((key) => keys.add(key));
        return;
      }
    });
    return Array.from(keys);
  }, [prompts]);

  const allImageKeys = useMemo(() => {
    const keys = new Set<string>();
    prompts.forEach((prompt) => {
      prompt.inputs_img.forEach((key) => keys.add(key));
      if (prompt.archivo.toLowerCase().endsWith('.png')) {
        keys.add(prompt.archivo);
      }
    });
    return Array.from(keys);
  }, [prompts]);

  const allTextInfoKeys = useMemo(() => {
    const keys = new Set<string>();
    allInputKeys.forEach((key) => {
      if (!allImageKeys.includes(key)) {
        keys.add(key);
      }
    });
    allOutputKeys.forEach((key) => {
      if (!allImageKeys.includes(key)) {
        keys.add(key);
      }
    });
    return Array.from(keys);
  }, [allInputKeys, allOutputKeys, allImageKeys]);

  const applyPromptTemplateValues = useCallback(
    (template: string, sourceValues?: Record<string, string>) =>
      template.replace(/\{([^\s{}\r\n]+)\}/g, (fullMatch, rawKey) => {
        const key = String(rawKey ?? '').trim();
        if (!key) {
          return fullMatch;
        }
        const values = sourceValues ?? infoDraftValues;
        const value = values[key];
        if (value == null || String(value).length === 0) {
          return fullMatch;
        }
        return String(value);
      }),
    [infoDraftValues]
  );

  useEffect(() => {
    if (!selectedPrompt) {
      setPromptOverride('');
      return;
    }

    const promptText = applyPromptTemplateValues(selectedPrompt.prompt);
    setPromptOverride(promptText);
  }, [selectedPrompt, applyPromptTemplateValues]);

  const showStatus = (message: string, type: ToastType, context: StatusContext = 'general') => {
    setStatus(message);
    setStatusType(type);
    setStatusContext(context);
  };

  const getTextSourceLabel = (
    key: string
  ): { label: 'Faltante' | 'Original' | 'Regenerado' | 'Forzado' | 'Preview'; className: string } => {
    if (Object.prototype.hasOwnProperty.call(textForcedOverrides, key)) {
      return { label: 'Forzado', className: 'source-pill-forced' };
    }
    if (Object.prototype.hasOwnProperty.call(sessionOutputValues, key)) {
      return { label: 'Regenerado', className: 'source-pill-regenerated' };
    }
    if (Object.prototype.hasOwnProperty.call(rootOutputValues, key)) {
      return { label: 'Original', className: 'source-pill-original' };
    }
    if (Object.prototype.hasOwnProperty.call(previewOutputValues, key)) {
      return { label: 'Preview', className: 'source-pill-original' };
    }
    return { label: 'Faltante', className: 'source-pill-missing' };
  };

  const getImageSourceLabel = (
    key: string
  ): { label: 'Faltante' | 'Original' | 'Regenerado' | 'Forzado' | 'Preview'; className: string } => {
    if (Object.prototype.hasOwnProperty.call(imageForcedOverrides, key)) {
      return { label: 'Forzado', className: 'source-pill-forced' };
    }
    if (imageAvailability[key]?.existsSession) {
      return { label: 'Regenerado', className: 'source-pill-regenerated' };
    }
    if (imageAvailability[key]?.existsRoot) {
      return { label: 'Original', className: 'source-pill-original' };
    }
    if (imageAvailability[key]?.existsPreview) {
      return { label: 'Preview', className: 'source-pill-original' };
    }
    return { label: 'Faltante', className: 'source-pill-missing' };
  };

  const updateTextValue = (key: string, value: string) => {
    setInfoDraftValues((prev) => {
      const next = { ...prev, [key]: value };
      if (selectedPrompt) {
        setPromptOverride(applyPromptTemplateValues(selectedPrompt.prompt, next));
      }
      return next;
    });
    const resolved = resolveValue(key);
    const baseline = resolved.value == null ? '' : formatValuePreview(resolved.value);
    setTextForcedOverrides((prev) => {
      const next = { ...prev };
      if (value !== baseline) {
        next[key] = value;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const getTextOverrides = () =>
    Object.fromEntries(
      Object.entries(infoDraftValues).filter(([key, value]) => {
        if (allImageKeys.includes(key)) {
          return false;
        }
        return value !== (infoSourceValues[key] ?? '');
      })
    );

  const loadArtifactsAndOutputs = async (
    project: string,
    regen: string,
    selectedPreviewId: number | null,
    flowPrompts: FlowPrompt[]
  ): Promise<ArtifactLoadResult> => {
    const rootFolderId = getFolderId(project, '');
    const sessionFolderId = regen ? getFolderId(project, regen) : '';
    const previewFolderId = selectedPreviewId ? `${project}/Preview_${selectedPreviewId}` : '';

    const nextArtifacts: Record<string, PromptArtifactState> = {};
    const nextRootValues: Record<string, string> = {};
    const nextSessionValues: Record<string, string> = {};
    const nextPreviewValues: Record<string, string> = {};
    const nextImageAvailability: Record<string, PromptArtifactState> = {};
    const nextManualOverrides: Record<string, string> = {};

    // Base questionnaire is always read from project root (never from regeneration folder).
    const cuestionarioUrl = await getFileUrlIfExists(`${rootFolderId}/JSONS/cuestionario.json`);
    if (cuestionarioUrl) {
      const cuestionarioMap = await readJsonTopLevelMap(cuestionarioUrl);
      Object.assign(nextRootValues, cuestionarioMap);
    }

    if (sessionFolderId) {
      const manualOverridesUrl = await getFileUrlIfExists(`${sessionFolderId}/JSONS/manual_overrides.json`);
      if (manualOverridesUrl) {
        Object.assign(nextManualOverrides, await readJsonTopLevelMap(manualOverridesUrl));
      }
    }

    await Promise.all(
      flowPrompts.map(async (prompt) => {
        const artifactPathRoot = buildArtifactPath(rootFolderId, prompt.archivo);
        const rootUrl = await getFileUrlIfExists(artifactPathRoot);
        const sessionUrl = sessionFolderId
          ? await getFileUrlIfExists(buildArtifactPath(sessionFolderId, prompt.archivo))
          : '';
        const previewUrl = previewFolderId
          ? await getFileUrlIfExists(buildArtifactPath(previewFolderId, prompt.archivo))
          : '';

        nextArtifacts[prompt.rowKey] = {
          existsRoot: Boolean(rootUrl),
          existsSession: Boolean(sessionUrl),
          existsPreview: Boolean(previewUrl),
        };

        if (prompt.archivo.toLowerCase().endsWith('.json')) {
          if (rootUrl) {
            const rootJson = await readJsonTopLevelMap(rootUrl);
            prompt.outputs.forEach((key) => {
              if (Object.prototype.hasOwnProperty.call(rootJson, key)) {
                nextRootValues[key] = rootJson[key];
              }
            });
          }
          if (sessionUrl) {
            const sessionJson = await readJsonTopLevelMap(sessionUrl);
            prompt.outputs.forEach((key) => {
              if (Object.prototype.hasOwnProperty.call(sessionJson, key)) {
                nextSessionValues[key] = sessionJson[key];
              }
            });
          }
          if (previewUrl) {
            const previewJson = await readJsonTopLevelMap(previewUrl);
            prompt.outputs.forEach((key) => {
              if (Object.prototype.hasOwnProperty.call(previewJson, key)) {
                nextPreviewValues[key] = previewJson[key];
              }
            });
          }
          return;
        }

        if (prompt.archivo.toLowerCase().endsWith('.png')) {
          if (rootUrl) {
            nextRootValues[prompt.archivo] = prompt.archivo;
          }
          if (sessionUrl) {
            nextSessionValues[prompt.archivo] = prompt.archivo;
          }
          if (previewUrl) {
            nextPreviewValues[prompt.archivo] = prompt.archivo;
          }
          nextImageAvailability[prompt.archivo] = {
            existsRoot: Boolean(rootUrl),
            existsSession: Boolean(sessionUrl),
            existsPreview: Boolean(previewUrl),
          };
        }
      })
    );
    const inputImageKeys = Array.from(
      new Set(flowPrompts.flatMap((prompt) => prompt.inputs_img.map((item) => item.trim()).filter(Boolean)))
    );
    await Promise.all(
      inputImageKeys.map(async (imageKey) => {
        const rootUrl = await getFileUrlIfExists(`${rootFolderId}/IMGS/${imageKey}`);
        const sessionUrl = sessionFolderId ? await getFileUrlIfExists(`${sessionFolderId}/IMGS/${imageKey}`) : '';
        const previewUrl = previewFolderId ? await getFileUrlIfExists(`${previewFolderId}/IMGS/${imageKey}`) : '';
        const previous = nextImageAvailability[imageKey] ?? {
          existsRoot: false,
          existsSession: false,
          existsPreview: false,
        };
        nextImageAvailability[imageKey] = {
          existsRoot: previous.existsRoot || Boolean(rootUrl),
          existsSession: previous.existsSession || Boolean(sessionUrl),
          existsPreview: previous.existsPreview || Boolean(previewUrl),
        };
      })
    );

    return {
      nextArtifacts,
      nextRootValues,
      nextSessionValues,
      nextPreviewValues,
      nextImageAvailability,
      nextManualOverrides,
    };
  };

  const refreshEmailAssets = useCallback(async (project: string, regen: string) => {
    const normalizedProjectId = project.trim();
    const normalizedRegenerationId = regen.trim();
    if (!normalizedProjectId || !normalizedRegenerationId) {
      setEmailAssetsBrandPdfUrl('');
      setEmailAssetsRightsPdfUrl('');
      setEmailAssetsContentPdfUrl('');
      setEmailAssetsZipUrl('');
      return;
    }

    const folderId = `${normalizedProjectId}/${normalizedRegenerationId}`;
    const [brandPdfUrl, rightsPdfUrl, contentPdfUrl, zipUrl] = await Promise.all([
      getFileUrlIfExists(`${folderId}/PDF/BRAND.pdf`),
      getFileUrlIfExists(`${folderId}/PDF/RIGHTS.pdf`),
      getFileUrlIfExists(`${folderId}/PDF/CONTENT.pdf`),
      getFileUrlIfExists(`${folderId}/${normalizedProjectId}_assets.zip`),
    ]);

    setEmailAssetsBrandPdfUrl(brandPdfUrl);
    setEmailAssetsRightsPdfUrl(rightsPdfUrl);
    setEmailAssetsContentPdfUrl(contentPdfUrl);
    setEmailAssetsZipUrl(zipUrl);
    setGenerateAssets({
      brandPdf: true,
      rightsPdf: true,
      contentPdf: true,
      zip: true,
      logoVariants: false,
    });
  }, []);

  const loadManualData = async (
    overrideRegenerationId?: string,
    options?: { preserveOverrides?: boolean }
  ) => {
    const preserveOverrides = Boolean(options?.preserveOverrides);
    const normalizedProjectId = projectId.trim();
    const normalizedRegenerationId = (overrideRegenerationId ?? regenerationId).trim();
    if (!normalizedProjectId) {
      setIsInfoLoaded(false);
      setSelectedPreview(null);
      showStatus('Ingresa un Project ID válido.', 'error', 'load');
      return;
    }

    setIsLoading(true);
    setStatus('');
    setRequiresTypeSelection(false);

    try {
      const [projectSnapshot, queueSnapshot, queuePreviewSnapshot] = await Promise.all([
        get(ref(database, `Projects/${normalizedProjectId}`)),
        get(ref(database, 'Queue')),
        get(ref(database, 'QueuePreview')),
      ]);

      const projectValue = projectSnapshot.val() as Record<string, unknown> | null;
      if (!projectValue) {
        setIsInfoLoaded(false);
        setSelectedPreview(null);
        setRequiresTypeSelection(false);
        showStatus('No se encontró el proyecto.', 'error', 'load');
        return;
      }

      const rawSelectedPreview = Number(projectValue.selected_preview);
      const selectedPreviewId =
        Number.isInteger(rawSelectedPreview) && rawSelectedPreview >= 1 ? rawSelectedPreview : null;

      const nextProjectType = String(projectValue.type ?? '').trim() as ProjectType;
      if (nextProjectType !== 'Global' && nextProjectType !== 'Standard') {
        setIsInfoLoaded(false);
        setProjectType(null);
        setSelectedPreview(null);
        const typePrice = String(projectValue.type_price ?? '').trim();
        if (typePrice === 'Admin_Standard') {
          setManualTypeSelection('Standard');
        } else {
          setManualTypeSelection('Global');
        }
        setRequiresTypeSelection(true);
        showStatus('No se pudo determinar el tipo del proyecto.', 'error', 'type');
        return;
      }
      setRequiresTypeSelection(false);

      const [previewPromptsSnapshot, fullPromptsSnapshot] = await Promise.all([
        get(ref(database, 'PromptsPreview')),
        get(ref(database, nextProjectType === 'Global' ? 'PromptsGlobal' : 'PromptsStandard')),
      ]);

      const previewPrompts = parsePromptTemplates(
        previewPromptsSnapshot.val() as Record<string, unknown> | null
      ).map((prompt) => ({ ...prompt, scope: 'preview' as const, rowKey: `preview-${prompt.id}` }));

      const fullPrompts = parsePromptTemplates(
        fullPromptsSnapshot.val() as Record<string, unknown> | null
      ).map((prompt) => ({ ...prompt, scope: 'full' as const, rowKey: `full-${prompt.id}` }));

      const flowPrompts = [...previewPrompts, ...fullPrompts];

      const queueValue = (queueSnapshot.val() ?? {}) as QueueNode;
      const queuePreviewValue = (queuePreviewSnapshot.val() ?? {}) as QueuePreviewNode;
      const inQueue =
        containsProjectId(queueValue.projectsGlobal, normalizedProjectId) ||
        containsProjectId(queueValue.projectsStandard, normalizedProjectId) ||
        containsProjectId(queuePreviewValue.projects, normalizedProjectId);
      const running =
        isRunningProject(queueValue.run, normalizedProjectId) ||
        isRunningProject(queuePreviewValue.run, normalizedProjectId);
      const locked = inQueue || running;

      const artifactData = await loadArtifactsAndOutputs(
        normalizedProjectId,
        normalizedRegenerationId,
        selectedPreviewId,
        flowPrompts
      );
      setPromptArtifacts(artifactData.nextArtifacts);
      setRootOutputValues(artifactData.nextRootValues);
      setSessionOutputValues(artifactData.nextSessionValues);
      setPreviewOutputValues(artifactData.nextPreviewValues);
      setImageAvailability(artifactData.nextImageAvailability);
      await refreshEmailAssets(normalizedProjectId, normalizedRegenerationId);

      setProjectType(nextProjectType);
      setSelectedPreview(selectedPreviewId);
      setPrompts(flowPrompts);
      setSelectedPromptKey((prev) => {
        if (prev && flowPrompts.some((prompt) => prompt.rowKey === prev)) {
          return prev;
        }
        return flowPrompts[0]?.rowKey || '';
      });
      setRegenerationIds(getRegenerationIdsFromProject(projectValue));

      const nombre = String(projectValue.Nombre ?? '').trim();
      const email = String(projectValue.Email ?? '').trim();
      const languageGuess: LanguageType = String(
        artifactData.nextSessionValues.LANGUAGE ??
          artifactData.nextRootValues.LANGUAGE ??
          artifactData.nextPreviewValues.LANGUAGE ??
          ''
      ).toLowerCase() === 'en'
        ? 'en'
        : 'es';

      setProjectNombre(nombre);
      setProjectEmail(email);
      setProjectLanguage(languageGuess);
      setSendName((prev) => (prev.trim() ? prev : nombre));
      setSendEmail((prev) => (prev.trim() ? prev : email));
      setSendLanguage(languageGuess);

      setIsQueueLocked(locked);
      setQueueLockMessage(
        locked
          ? 'El proyecto está en cola o en ejecución. Generación manual bloqueada temporalmente.'
          : ''
      );
      setIsInfoLoaded(true);

      const allTextKeysForDraft = new Set<string>();
      flowPrompts.forEach((prompt) => {
        prompt.inputs.forEach((key) => allTextKeysForDraft.add(key));
        extractPromptVariables(prompt.prompt).forEach((key) => allTextKeysForDraft.add(key));
        if (prompt.archivo.toLowerCase().endsWith('.json')) {
          prompt.outputs.forEach((key) => allTextKeysForDraft.add(key));
        }
      });
      const initialDraftValues = Object.fromEntries(
        Array.from(allTextKeysForDraft).map((key) => {
          const value =
            artifactData.nextSessionValues[key] ??
            artifactData.nextRootValues[key] ??
            artifactData.nextPreviewValues[key] ??
            '';
          return [key, value];
        })
      );
      allImageKeys.forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(initialDraftValues, key)) {
          initialDraftValues[key] =
            artifactData.nextSessionValues[key] ??
            artifactData.nextRootValues[key] ??
            artifactData.nextPreviewValues[key] ??
            key;
        }
      });
      setInfoSourceValues(initialDraftValues);
      setInfoDraftValues({
        ...initialDraftValues,
        ...artifactData.nextManualOverrides,
      });
      setTextForcedOverrides(artifactData.nextManualOverrides);
      if (!preserveOverrides) {
        setImageForcedOverrides({});
        setInputImageOverride(null);
      }

      showStatus(
        normalizedRegenerationId
          ? `Proyecto cargado. Sesión ${normalizedRegenerationId} activa.`
          : 'Proyecto cargado. Puedes iniciar sesión de regeneración con run_prompt.',
        'success',
        'load'
      );
    } catch (error) {
      setIsInfoLoaded(false);
      setSelectedPreview(null);
      showStatus('Error al cargar datos de generación manual.', 'error', 'load');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFileFromUrl = (url: string, fileName: string) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleSaveProjectType = async () => {
    const user = auth.currentUser;
    if (!user) {
      showStatus('Inicia sesión para actualizar el tipo del proyecto.', 'error', 'type');
      return;
    }

    const normalizedProjectId = projectId.trim();
    if (!normalizedProjectId) {
      showStatus('Ingresa un Project ID válido.', 'error', 'type');
      return;
    }

    const selectedType: ProjectType = manualTypeSelection;
    const typePrice = selectedType === 'Global' ? 'Admin_Global' : 'Admin_Standard';

    setIsSavingProjectType(true);
    showStatus('Guardando tipo del proyecto...', 'neutral', 'type');

    try {
      await update(ref(database), {
        [`Projects/${normalizedProjectId}/type`]: selectedType,
        [`Projects/${normalizedProjectId}/type_price`]: typePrice,
        [`ProjectType/${typePrice}/${normalizedProjectId}`]: true,
      });
      showStatus('Tipo guardado. Recargando proyecto...', 'success', 'type');
      await loadManualData();
    } catch (error) {
      showStatus('No se pudo guardar el tipo del proyecto.', 'error', 'type');
    } finally {
      setIsSavingProjectType(false);
    }
  };

  const handleImageOverridesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const targetKey = event.target.dataset.imageKey || '';
    if (!files.length) {
      return;
    }
    if (!targetKey) {
      event.target.value = '';
      return;
    }
    try {
      const first = files[0];
      const dataUrl = await readFileAsDataUrl(first);
      const keyFromProjectInfo = (infoDraftValues[targetKey] ?? targetKey).trim();
      setImageForcedOverrides((prev) => ({
        ...prev,
        [targetKey]: {
          fileName: keyFromProjectInfo || targetKey,
          base64: toBase64Content(dataUrl),
        },
      }));
    } catch (error) {
      showStatus('No se pudieron leer las imágenes para imageOverrides.', 'error', 'run');
    } finally {
      event.target.value = '';
    }
  };

  const handleInputImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setInputImageOverride({
        fileName: file.name,
        base64: toBase64Content(dataUrl),
        previewUrl: dataUrl,
      });
    } catch (error) {
      showStatus('No se pudo leer inputImage.', 'error', 'run');
    } finally {
      event.target.value = '';
    }
  };

  const handleRunPrompt = async () => {
    const user = auth.currentUser;
    if (!user) {
      showStatus('Inicia sesión para correr prompts.', 'error', 'run');
      return;
    }
    if (isQueueLocked) {
      showStatus('No se puede correr generación manual mientras el proyecto está en cola.', 'warning', 'run');
      return;
    }
    if (!selectedPrompt) {
      showStatus('Selecciona un prompt para correr.', 'error', 'run');
      return;
    }

    const normalizedProjectId = projectId.trim();
    if (!normalizedProjectId) {
      showStatus('Ingresa un Project ID válido.', 'error', 'run');
      return;
    }

    setIsRunningPrompt(true);
    showStatus('Ejecutando run_prompt...', 'neutral', 'run');

    try {
      const token = await user.getIdToken();
      const imageOverrides = Object.fromEntries(
        Object.values(imageForcedOverrides)
          .filter((item) => item.fileName.trim().length > 0 && item.base64.trim().length > 0)
          .map((item) => [item.fileName.trim(), { base64: item.base64 }])
      );
      const textOverrides = getTextOverrides();

      const body: Record<string, unknown> = {
        action: 'run_prompt',
        projectId: normalizedProjectId,
        regenerationId: regenerationId.trim(),
        scope: selectedPrompt.scope,
        stepNum: selectedPrompt.id,
        promptOverride,
        textOverrides,
      };
      if (inputImageOverride?.base64.trim()) {
        body.inputImage = { base64: inputImageOverride.base64 };
      }
      if (Object.keys(imageOverrides).length) {
        body.imageOverrides = imageOverrides;
      }

      const response = await fetch(partialGenerationEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const backendMessage = (await response.text().catch(() => '')).trim();
        showStatus(
          backendMessage
            ? `Error run_prompt: ${backendMessage}`
            : `Error en run_prompt (HTTP ${response.status}).`,
          'error',
          'run'
        );
        return;
      }

      const payload = (await response.json()) as RunPromptResponse;
      const nextRegenerationId = String(payload.regenerationId ?? '').trim();
      if (nextRegenerationId) {
        setRegenerationId(nextRegenerationId);
      }
      await loadManualData(nextRegenerationId || regenerationId.trim(), { preserveOverrides: true });
      showStatus(
        nextRegenerationId
          ? `run_prompt completado. regenerationId: ${nextRegenerationId}`
          : 'run_prompt completado.',
        'success',
        'run'
      );
    } catch (error) {
      showStatus('Error al ejecutar run_prompt.', 'error', 'run');
    } finally {
      setIsRunningPrompt(false);
    }
  };

  const handleDelivery = async () => {
    const user = auth.currentUser;
    if (!user) {
      showStatus('Inicia sesión para enviar el resultado final.', 'error', 'send');
      return;
    }
    if (isQueueLocked) {
      showStatus('No se puede enviar mientras el proyecto está en cola o en ejecución.', 'warning', 'send');
      return;
    }

    const normalizedProjectId = projectId.trim();
    const normalizedRegenerationId = regenerationId.trim();
    if (!normalizedProjectId) {
      showStatus('Ingresa un Project ID válido.', 'error', 'send');
      return;
    }
    if (hasEmailAssets && (!sendName.trim() || !sendEmail.trim())) {
      showStatus('name y email son obligatorios.', 'error', 'send');
      return;
    }
    if (!hasEmailAssets && !Object.values(generateAssets).some(Boolean)) {
      showStatus('Selecciona al menos un asset para generar.', 'error', 'send');
      return;
    }

    setIsSendingFinal(true);
    showStatus(hasEmailAssets ? 'Enviando resultado final...' : 'Generando assets...', 'neutral', 'send');

    try {
      const token = await user.getIdToken();
      const body: Record<string, unknown> = hasEmailAssets
        ? {
            action: 'send',
            projectId: normalizedProjectId,
            regenerationId: normalizedRegenerationId,
            name: sendName.trim(),
            email: sendEmail.trim(),
            language: sendLanguage,
          }
        : {
            action: 'generate',
            projectId: normalizedProjectId,
            regenerationId: normalizedRegenerationId,
            generateBrandPdf: generateAssets.brandPdf,
            generateRightsPdf: generateAssets.rightsPdf,
            generateContentPdf: generateAssets.contentPdf,
            generateZip: generateAssets.zip,
            generateLogoVariants: generateAssets.logoVariants,
            textOverrides: getTextOverrides(),
            ...(sendName.trim() ? { name: sendName.trim() } : {}),
            ...(sendEmail.trim() ? { email: sendEmail.trim() } : {}),
            ...(sendLanguage ? { language: sendLanguage } : {}),
          };
      const response = await fetch(partialGenerationEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const backendText = (await response.text().catch(() => '')).trim();
        let backendMessage = backendText;
        try {
          const backendPayload = JSON.parse(backendText) as SendResponse;
          if (Array.isArray(backendPayload.missingAssets)) {
            backendMessage = `Faltan assets: ${backendPayload.missingAssets.join(', ')}`;
          }
        } catch (error) {
          // The endpoint can return a non-JSON error response.
        }
        showStatus(
          backendMessage
            ? `Error ${hasEmailAssets ? 'send' : 'generate'}: ${backendMessage}`
            : `Error en ${hasEmailAssets ? 'send' : 'generate'} (HTTP ${response.status}).`,
          'error',
          'send'
        );
        return;
      }

      const payload = (await response.json()) as SendResponse;
      const nextRegenerationId = String(payload.regenerationId ?? '').trim();
      if (nextRegenerationId) {
        setRegenerationId(nextRegenerationId);
      }
      await refreshEmailAssets(normalizedProjectId, nextRegenerationId || normalizedRegenerationId);
      showStatus(hasEmailAssets ? 'Correo enviado.' : 'Assets generados.', 'success', 'send');
    } catch (error) {
      showStatus('Error en la operación.', 'error', 'send');
    } finally {
      setIsSendingFinal(false);
    }
  };

  const promptStatusRows = useMemo(
    () =>
      prompts.map((prompt) => {
        const artifact = promptArtifacts[prompt.rowKey];
        let statusLabel: PromptStatus = 'Pendiente';
        if (artifact?.existsSession) {
          statusLabel = 'Regenerado';
        } else if (artifact?.existsRoot || artifact?.existsPreview) {
          statusLabel = 'Ejecutado';
        }
        return { prompt, statusLabel };
      }),
    [prompts, promptArtifacts]
  );

  return (
    <div className="section">
      <div className="section-header">
        <h2>Generación manual</h2>
      </div>

      <form
        className="project-search"
        onSubmit={(event) => {
          event.preventDefault();
          void loadManualData();
        }}
      >
        <label className="field">
          <span>Project ID</span>
          <input
            className="table-input"
            type="text"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            placeholder="ID del proyecto"
          />
        </label>
        <label className="field">
          <span>Regeneration ID (opcional)</span>
          <input
            className="table-input"
            type="text"
            value={regenerationId}
            onChange={(event) => setRegenerationId(event.target.value)}
            placeholder="Vacío para nueva sesión"
          />
        </label>
        <button className="primary-button" type="submit" disabled={isLoading || isRunningPrompt || isSendingFinal}>
          {isLoading ? 'Cargando...' : 'Cargar info'}
        </button>
      </form>
      {statusContext === 'load' && status ? (
        <div className={`inline-toast inline-toast-${statusType}`}>{status}</div>
      ) : null}

      {requiresTypeSelection ? (
        <div className="project-table">
          <div className="project-table-header">
            <h3>Definir tipo del proyecto</h3>
            <div className="panel-actions-inline">
              <label>
                <input
                  type="radio"
                  name="manual-project-type"
                  value="Global"
                  checked={manualTypeSelection === 'Global'}
                  onChange={() => setManualTypeSelection('Global')}
                  disabled={isSavingProjectType}
                />{' '}
                Global
              </label>
              <label>
                <input
                  type="radio"
                  name="manual-project-type"
                  value="Standard"
                  checked={manualTypeSelection === 'Standard'}
                  onChange={() => setManualTypeSelection('Standard')}
                  disabled={isSavingProjectType}
                />{' '}
                Standard
              </label>
              <button
                className="primary-button"
                type="button"
                onClick={handleSaveProjectType}
                disabled={isSavingProjectType || isLoading}
              >
                {isSavingProjectType ? 'Guardando...' : 'Guardar tipo'}
              </button>
            </div>
          </div>
          {statusContext === 'type' && status ? (
            <div className={`inline-toast inline-toast-${statusType}`}>{status}</div>
          ) : null}
        </div>
      ) : null}
      {isInfoLoaded ? (
        <>
      {!hasEmailAssets ? (
        <>
          {projectType ? <div className="hint">Tipo: {projectType}</div> : null}
          {selectedPreview !== null ? <div className="hint">Preview seleccionado: {selectedPreview}</div> : null}
          {regenerationIds.length ? <div className="hint">Regeneraciones: {regenerationIds.join(', ')}</div> : null}
          {projectNombre || projectEmail ? (
            <div className="hint">
              {projectNombre || '-'} | {projectEmail || '-'} | {projectLanguage}
            </div>
          ) : null}

          {allTextInfoKeys.length || allImageKeys.length ? (
            <div className="project-table">
              <div className="project-table-header">
                <h3>Info del proyecto</h3>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setIsInfoExpanded((prev) => !prev)}
                >
                  {isInfoExpanded ? 'Colapsar' : 'Expandir'}
                </button>
              </div>
              {isInfoExpanded ? (
              <div className="table table-compact">
                <div className="table-row table-header manual-io-columns">
                  <div>Clave</div>
                  <div>Value</div>
                  <div>Fuente</div>
                </div>
                {allTextInfoKeys.map((key) => {
                  const sourceInfo = getTextSourceLabel(key);
                  return (
                    <div className="table-row manual-io-columns" key={`txt-${key}`}>
                      <div className="mono">{key}</div>
                      <div>
                        <input
                          className="table-input"
                          type="text"
                          value={infoDraftValues[key] ?? ''}
                          onChange={(event) => updateTextValue(key, event.target.value)}
                        />
                      </div>
                      <div>
                        <span className={`source-pill ${sourceInfo.className}`}>{sourceInfo.label}</span>
                      </div>
                    </div>
                  );
                })}
                {allImageKeys.map((key) => {
                  const sourceInfo = getImageSourceLabel(key);
                  const hasForced = Boolean(imageForcedOverrides[key]);
                  return (
                    <div className="table-row manual-io-columns" key={`img-${key}`}>
                      <div className="mono">{key}</div>
                      <div className="manual-image-value-cell">
                        <input
                          className="table-input"
                          type="text"
                          value={hasForced ? imageForcedOverrides[key].fileName : key}
                          readOnly
                        />
                        <input
                          className="table-input"
                          type="file"
                          accept="image/*"
                          data-image-key={key}
                          onChange={handleImageOverridesChange}
                        />
                        {hasForced ? (
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() =>
                              setImageForcedOverrides((prev) => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                              })
                            }
                          >
                            Limpiar
                          </button>
                        ) : null}
                      </div>
                      <div>
                        <span className={`source-pill ${sourceInfo.className}`}>{sourceInfo.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              ) : null}
            </div>
          ) : null}

          {prompts.length ? (
            <div className="project-table">
              <div className="project-table-header">
                <h3>Prompts del flujo</h3>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setIsPromptsExpanded((prev) => !prev)}
                >
                  {isPromptsExpanded ? 'Colapsar' : 'Expandir'}
                </button>
              </div>
              {isPromptsExpanded ? (
              <div className="table table-compact">
                <div className="table-row table-header manual-prompts-columns">
                  <div>Estado</div>
                  <div>Scope</div>
                  <div>ID</div>
                  <div>Nombre</div>
                  <div>Archivo</div>
                </div>
                {promptStatusRows.map((row) => (
                  <div
                    className={`table-row manual-prompts-columns manual-prompt-row ${
                      selectedPromptKey === row.prompt.rowKey ? 'manual-prompt-row-selected' : ''
                    }`}
                    key={`prompt-row-${row.prompt.rowKey}`}
                    onClick={() => setSelectedPromptKey(row.prompt.rowKey)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedPromptKey(row.prompt.rowKey);
                      }
                    }}
                  >
                    <div>
                      <span
                        className={`status-pill ${
                          row.statusLabel === 'Regenerado'
                            ? 'status-pill-regenerated'
                            : row.statusLabel === 'Ejecutado'
                              ? 'status-pill-executed'
                              : 'status-pill-pending'
                        }`}
                      >
                        {row.statusLabel}
                      </span>
                    </div>
                    <div>{row.prompt.scope}</div>
                    <div className="mono">{row.prompt.id}</div>
                    <div>{row.prompt.nombre || '-'}</div>
                    <div className="mono">{row.prompt.archivo || '-'}</div>
                  </div>
                ))}
              </div>
              ) : null}
            </div>
          ) : null}

          {selectedPrompt ? (
            <div className="project-table">
              <div className="project-table-header">
                <h3>
                  Correr Prompt {selectedPrompt.scope}:{selectedPrompt.id} - {selectedPrompt.nombre || '-'}
                </h3>
                <div className="manual-run-actions">
                  <button
                    className="primary-button"
                    type="button"
                    onClick={handleRunPrompt}
                    disabled={isRunningPrompt || isSendingFinal || isQueueLocked}
                  >
                    {isRunningPrompt ? 'Ejecutando...' : 'Correr prompt'}
                  </button>
                  <span className="manual-credits-pill">Gemini AI usage</span>
                </div>
              </div>

              <div className="table table-compact">
                <div className="table-row table-two-columns">
                  <div>Prompt</div>
                  <div>
                    <textarea
                      className="table-input prompt-tester-textarea"
                      value={promptOverride}
                      onChange={(event) => setPromptOverride(event.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="table table-compact">
                <div className="table-row table-two-columns">
                  <div>Imagen input</div>
                  <div>
                    <input
                      className="table-input"
                      type="file"
                      accept="image/*"
                      onChange={handleInputImageChange}
                    />
                    {inputImageOverride ? (
                      <div className="prompt-result-card">
                        <div className="mono">{inputImageOverride.fileName}</div>
                        <img
                          className="prompt-result-image"
                          src={inputImageOverride.previewUrl}
                          alt="Input image preview"
                        />
                        <div className="panel-actions-inline">
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => setInputImageOverride(null)}
                          >
                            Limpiar imagen input
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {statusContext === 'run' && status ? (
                <div className={`inline-toast inline-toast-${statusType}`}>{status}</div>
              ) : null}
              {isQueueLocked ? <div className="inline-toast inline-toast-warning">{queueLockMessage}</div> : null}
            </div>
          ) : null}
        </>
      ) : (
        <div className="hint">
          Si quieres editar esta sesión de regeneración, borra uno o más de estos archivos de la
          carpeta de regeneración en firebase: BRAND.pdf, RIGHTS.pdf, CONTENT.pdf y el archivo .zip.
        </div>
      )}

      <div className="project-table">
        <div className="project-table-header">
          <h3>{hasEmailAssets ? 'Enviar correo' : 'Generar assets'}</h3>
          <div className="panel-actions-inline">
            <button
              className="primary-button"
              type="button"
              onClick={handleDelivery}
              disabled={isSendingFinal || isRunningPrompt || isQueueLocked}
            >
              {isSendingFinal
                ? hasEmailAssets
                  ? 'Enviando...'
                  : 'Generando...'
                : hasEmailAssets
                  ? 'Enviar email'
                  : 'Generar assets para email'}
            </button>
          </div>
        </div>
        <div className="table table-compact">
          {!hasEmailAssets ? (
            <>
              <div className="table-row table-two-columns">
                <div>Assets a generar</div>
                <div className="manual-generate-options">
                  <label>
                    <input
                      type="checkbox"
                      checked={generateAssets.brandPdf}
                      onChange={(event) =>
                        setGenerateAssets((prev) => ({ ...prev, brandPdf: event.target.checked }))
                      }
                    />{' '}
                    PDF Brand
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={generateAssets.contentPdf}
                      onChange={(event) =>
                        setGenerateAssets((prev) => ({ ...prev, contentPdf: event.target.checked }))
                      }
                    />{' '}
                    PDF Content
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={generateAssets.rightsPdf}
                      onChange={(event) =>
                        setGenerateAssets((prev) => ({ ...prev, rightsPdf: event.target.checked }))
                      }
                    />{' '}
                    PDF Rights
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={generateAssets.zip}
                      onChange={(event) =>
                        setGenerateAssets((prev) => ({ ...prev, zip: event.target.checked }))
                      }
                    />{' '}
                    ZIP
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={generateAssets.logoVariants}
                      onChange={(event) =>
                        setGenerateAssets((prev) => ({ ...prev, logoVariants: event.target.checked }))
                      }
                    />{' '}
                    Logos <span className="manual-credits-pill">Vectorizer AI credits</span>
                  </label>
                </div>
              </div>
            </>
          ) : null}
          <div className="table-row table-two-columns">
            <div>Nombre del usuario</div>
            <div>
              <input
                className="table-input"
                type="text"
                value={sendName}
                onChange={(event) => setSendName(event.target.value)}
                placeholder="Nombre destinatario"
              />
            </div>
          </div>
          <div className="table-row table-two-columns">
            <div>Email del usuario</div>
            <div>
              <input
                className="table-input"
                type="email"
                value={sendEmail}
                onChange={(event) => setSendEmail(event.target.value)}
                placeholder="correo@dominio.com"
              />
            </div>
          </div>
          <div className="table-row table-two-columns">
            <div>Idioma del correo</div>
            <div>
              <select
                className="table-input"
                value={sendLanguage}
                onChange={(event) => setSendLanguage(event.target.value as LanguageType)}
              >
                <option value="es">es</option>
                <option value="en">en</option>
              </select>
            </div>
          </div>
        </div>
        {hasEmailAssets ? (
          <div className="panel-actions-inline manual-download-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={() => downloadFileFromUrl(emailAssetsBrandPdfUrl, 'BRAND.pdf')}
              disabled={isSendingFinal || isRunningPrompt}
            >
              Descargar PDF Brand
            </button>
            <button
              className="secondary-button"
              type="button"
              onClick={() => downloadFileFromUrl(emailAssetsContentPdfUrl, 'CONTENT.pdf')}
              disabled={isSendingFinal || isRunningPrompt}
            >
              Descargar PDF Content
            </button>
            <button
              className="secondary-button"
              type="button"
              onClick={() => downloadFileFromUrl(emailAssetsRightsPdfUrl, 'RIGHTS.pdf')}
              disabled={isSendingFinal || isRunningPrompt}
            >
              Descargar PDF Rights
            </button>
            <button
              className="secondary-button"
              type="button"
              onClick={() => downloadFileFromUrl(emailAssetsZipUrl, `${projectId.trim()}_assets.zip`)}
              disabled={isSendingFinal || isRunningPrompt}
            >
              Descargar ZIP
            </button>
          </div>
        ) : null}
        {statusContext === 'send' && status ? (
          <div className={`inline-toast inline-toast-${statusType}`}>{status}</div>
        ) : null}
        {isQueueLocked ? <div className="inline-toast inline-toast-warning">{queueLockMessage}</div> : null}
      </div>
        </>
      ) : null}
    </div>
  );
}

export default ManualGenerationSection;
